import React from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, MousePointerClick, PencilLine, ClipboardCheck,
  Gift, ShieldCheck, Smartphone, BadgeCheck, HelpCircle, XCircle, Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { BuyButton } from "@/components/BuyButton";
import { PromptTool } from "@/components/PromptTool";
import { CATEGORIES } from "@/data/categories";

const PRICE = "R$ 4,99";

const PROBLEMS = [
  "Não sabe o que escrever?",
  "Não sabe como explicar?",
  "Perde tempo tentando montar um prompt?",
  "Quer algo mais organizado?",
];

const STEPS = [
  { Icon: MousePointerClick, title: "ESCOLHA", desc: "Selecione o tipo de tarefa." },
  { Icon: PencilLine, title: "RESPONDA", desc: "Informe algumas informações." },
  { Icon: ClipboardCheck, title: "USE", desc: "Copie o resultado e utilize onde quiser." },
];

const BONUSES = [
  { tag: "BÔNUS 1", text: "50 prompts prontos para situações do dia a dia." },
  { tag: "BÔNUS 2", text: "Guia rápido para escrever prompts melhores." },
  { tag: "BÔNUS 3", text: "Lista de ideias para usar IA no trabalho, estudos e organização." },
];

const FAQ = [
  { q: "O que é o Pronto.AI?", a: "O Pronto.AI é uma ferramenta online que ajuda você a transformar o que precisa em uma instrução (prompt) organizada e pronta para colar em ferramentas de inteligência artificial como ChatGPT, Gemini ou Claude. Você escolhe uma categoria, responde algumas perguntas simples e recebe o resultado pronto para copiar." },
  { q: "É um curso?", a: "Não. O Pronto.AI não é um curso. É uma ferramenta prática que você usa direto no navegador para gerar instruções prontas." },
  { q: "Funciona no celular?", a: "Sim. O Pronto.AI foi feito pensando primeiro no celular e funciona em iPhone, Android e nos navegadores modernos. Também funciona em computador e tablet." },
  { q: "Preciso entender de inteligência artificial?", a: "Não. Você só precisa escolher o que quer e responder perguntas simples. O Pronto.AI monta a instrução para você." },
  { q: "Como recebo o acesso?", a: "Após a compra, o acesso é apresentado de acordo com a plataforma de checkout utilizada. As condições de entrega e de acesso são informadas no próprio checkout." },
  { q: "Existe mensalidade?", a: "Não. O valor de R$ 4,99 é um pagamento único. Não há assinatura nem cobrança recorrente por parte da ferramenta." },
  { q: "Posso cancelar ou pedir reembolso?", a: "As condições de cancelamento e reembolso seguem a política da plataforma de pagamento utilizada no checkout e a legislação aplicável, como o Código de Defesa do Consumidor. Consulte as condições apresentadas no momento da compra." },
];

const TRUST = [
  { Icon: BadgeCheck, text: "Pagamento único de R$ 4,99" },
  { Icon: XCircle, text: "Sem mensalidade e sem assinatura" },
  { Icon: Smartphone, text: "Funciona no celular e no computador" },
  { Icon: ShieldCheck, text: "Transparente sobre o que você recebe" },
];

function PriceBadge() {
  return (
    <span className="inline-flex max-w-full flex-wrap items-center justify-center gap-x-2 gap-y-0.5 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-1.5 text-xs font-semibold text-emerald-400 sm:rounded-full sm:text-sm">
      <Sparkles className="h-4 w-4 shrink-0" />
      <span><span className="text-slate-500 line-through">R$ 19,99</span> por {PRICE}</span>
      <span className="text-emerald-400/70">• Pagamento único</span>
    </span>
  );
}

export default function Landing() {
  return (
    <main className="pt-16">
      {/* HERO */}
      <section className="grid-bg relative overflow-hidden px-4 pb-16 pt-14 sm:px-6 sm:pt-20">
        <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
          <div className="animate-float-up">
            <PriceBadge />
            <h1
              data-testid="hero-title"
              className="mt-6 font-display text-4xl font-extrabold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl"
            >
              Pare de perder tempo pensando no que escrever.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
              O Pronto.AI transforma o que você precisa em instruções prontas para usar com
              inteligência artificial.
            </p>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
              <BuyButton label="QUERO ACESSAR AGORA" source="hero" testId="hero-cta-button" />
              <Link to="/ferramenta" className="no-underline">
                <Button
                  variant="outline"
                  size="lg"
                  data-testid="hero-try-tool"
                  className="h-auto w-full rounded-full border-white/15 bg-transparent px-7 py-4 text-base text-slate-100 hover:bg-white/5 hover:text-white sm:w-auto"
                >
                  Testar a ferramenta
                </Button>
              </Link>
            </div>
            <p className="mt-4 text-xs text-slate-500" data-testid="hero-price-tag">
              {PRICE} — pagamento único, sem mensalidade.
            </p>
          </div>

          {/* Demonstração visual da ferramenta */}
          <div className="animate-float-up" style={{ animationDelay: "120ms" }}>
            <PromptTool enforceCredits={true} />
          </div>
        </div>
      </section>

      {/* PROBLEMA */}
      <section className="px-4 py-20 sm:px-6">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
            Você sabe o que quer, mas não sabe como pedir?
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-300">
            Às vezes você só precisa de uma mensagem, uma ideia, um texto ou uma solução rápida —
            mas não sabe por onde começar.
          </p>
          <div className="mx-auto mt-10 grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-2">
            {PROBLEMS.map((p) => (
              <div key={p} className="glass flex items-center gap-3 rounded-2xl px-5 py-4 text-left">
                <HelpCircle className="h-5 w-5 shrink-0 text-violet-400" />
                <span className="text-sm font-medium text-slate-200">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUÇÃO */}
      <section id="como-funciona" className="px-4 py-20 sm:px-6">
        <div className="mx-auto max-w-5xl text-center">
          <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
            É para isso que existe o Pronto.AI.
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-300">
            Escolha o que você precisa, responda algumas perguntas e receba uma instrução
            organizada e pronta para copiar.
          </p>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-3">
            {STEPS.map((s, i) => (
              <div key={s.title} className="glass rounded-3xl p-7 text-left">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500/20 to-cyan-400/20 text-violet-300">
                  <s.Icon className="h-6 w-6" />
                </span>
                <div className="mt-5 font-mono text-xs text-slate-500">0{i + 1}</div>
                <h3 className="mt-1 font-display text-xl font-bold text-white">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIAS */}
      <section id="categorias" className="px-4 py-20 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              8 categorias para qualquer necessidade
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-slate-300">
              Toque em uma categoria e comece a gerar na hora.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {CATEGORIES.map((cat) => (
              <Link
                key={cat.id}
                to={`/ferramenta?categoria=${cat.id}`}
                data-testid={`landing-category-${cat.id}`}
                className="group glass rounded-3xl p-5 no-underline transition-all hover:border-violet-500/50 hover:bg-violet-500/5 active:scale-[0.98]"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{cat.emoji}</span>
                  <cat.Icon className="h-5 w-5 text-violet-300" />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold text-white">{cat.name}</h3>
                <p className="mt-1 text-xs leading-relaxed text-slate-400">{cat.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* OFERTA */}
      <section id="oferta" className="px-4 py-20 sm:px-6">
        <div className="mx-auto max-w-3xl">
          <div className="glass relative overflow-hidden rounded-3xl p-6 text-center sm:p-12">
            <div className="pointer-events-none absolute inset-0 grid-bg opacity-60" />
            <div className="relative">
              <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                Comece por apenas
              </h2>
              <div className="mt-4 flex flex-wrap items-end justify-center gap-x-3 gap-y-1">
                <span className="font-display text-xl font-semibold text-slate-500 line-through sm:text-3xl" data-testid="offer-old-price">
                  R$ 19,99
                </span>
                <div className="font-display text-5xl font-extrabold text-white sm:text-7xl" data-testid="offer-price-tag">
                  <span className="gradient-text">{PRICE}</span>
                </div>
              </div>
              <p className="mt-3 text-base text-slate-300">
                Pagamento único por <span className="font-semibold text-white">1 ano de acesso</span>. Sem mensalidade.
              </p>

              <ul className="mx-auto mt-8 max-w-sm space-y-3 text-left">
                {[
                  "Acesso às 8 categorias da ferramenta",
                  "Mais de 50 modelos prontos para usar",
                  "Resultado organizado e pronto para copiar",
                  "Bônus digitais inclusos",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm text-slate-200">
                    <BadgeCheck className="h-5 w-5 shrink-0 text-emerald-400" />
                    {item}
                  </li>
                ))}
              </ul>

              <div className="mt-9 flex justify-center">
                <BuyButton label="QUERO MEU ACESSO" source="oferta" testId="offer-cta-button" />
              </div>
              <p className="mx-auto mt-5 max-w-md text-xs leading-relaxed text-slate-500">
                O acesso e as condições de compra devem ser apresentados de acordo com o checkout
                utilizado.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BÔNUS */}
      <section id="bonus" className="px-4 py-20 sm:px-6">
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Bônus digitais inclusos
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-3">
            {BONUSES.map((b) => (
              <div key={b.tag} className="glass rounded-3xl p-7">
                <span className="inline-flex items-center gap-2 rounded-full bg-violet-500/15 px-3 py-1 text-xs font-bold text-violet-300">
                  <Gift className="h-3.5 w-3.5" /> {b.tag}
                </span>
                <p className="mt-5 text-base leading-relaxed text-slate-200">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="px-4 py-20 sm:px-6">
        <div className="mx-auto max-w-3xl">
          <div className="text-center">
            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Perguntas frequentes
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-10 space-y-3">
            {FAQ.map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                data-testid={`faq-accordion-item-${i}`}
                className="glass overflow-hidden rounded-2xl border-white/8 px-5"
              >
                <AccordionTrigger className="py-5 text-left text-base font-semibold text-white hover:no-underline">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-sm leading-relaxed text-slate-300">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CONFIANÇA */}
      <section className="px-4 py-20 sm:px-6">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
            Feito para ser simples.
          </h2>
          <div className="mx-auto mt-10 grid max-w-3xl grid-cols-1 gap-3 sm:grid-cols-2">
            {TRUST.map((t) => (
              <div key={t.text} className="glass flex items-center gap-3 rounded-2xl px-5 py-4 text-left">
                <t.Icon className="h-5 w-5 shrink-0 text-emerald-400" />
                <span className="text-sm font-medium text-slate-200">{t.text}</span>
              </div>
            ))}
          </div>

          <div className="mt-12 flex justify-center">
            <BuyButton label="QUERO ACESSAR POR R$ 4,99" source="confianca" testId="trust-cta-button" />
          </div>
        </div>
      </section>
    </main>
  );
}
